<template>
    <transition :name="isLatestNotification ? 'latest-notification-transition' : 'v-fade-transition'" appear>
        <v-list-item
            class="px-3"
            :class="[
                isPopup ? 'sticky rounded-lg elevation-4' : '',
                isLatestNotification ? 'latest-notification' : '',
                isCpCommunicationNotification ? 'cp-communication-notification' : ''
            ]"
            @click="onNotificationClick(notification)"
        >
            <v-list-item-avatar class="my-0 align-self-start mt-2 mr-3 align-start">
                <AccountAvatar
                    v-if="showAccountAvatar()"
                    size="34"
                    text-color="neutral_n300"
                    text-class="font-weight-medium"
                    :color="notification.creator_avatar ? 'white' : 'neutral_n40'"
                    :initials="notification.creator_initials"
                    :image-path="notification.creator_avatar"
                />
                <v-img
                    v-else
                    src="@/assets/uku-logo.svg"
                    width="34"
                    height="34"
                    style="min-width: 34px; min-height: 34px; max-height: 34px; max-width: 34px;"
                />
                <div
                    v-if="isCpNotification"
                    class="rounded-circle cp-avatar-bubble"
                >
                    <icon
                        icon="Users"
                        size="12"
                        color="inverted"
                    />
                </div>
            </v-list-item-avatar>
            <v-list-item-content class="d-flex flex-nowrap">
                <div class="flex-fill">
                    <div class="body-2">
                        <div
                            :class="notification.is_seen ? 'g5--text' : ''"
                            class="notification-message mr-3"
                            v-html="notification.message"
                        ></div> <!-- INPUT CLEANED WHEN SERIALIZED FOR FRONTEND -->
                    </div>
                    <v-list-item-subtitle
                        class="caption d-flex align-center mt-1"
                        :class="!notification.is_seen ? 'primary--text' : 'g5--text'"
                    >
                        <div>
                            <span
                                class="font-weight-medium"
                                v-html="formatLastModifiedHtml(notification.created_at)"
                            ></span> <!-- INPUT FROM STATIC TRANSLATION -->
                            <span
                                v-if="isCpNotification"
                                class="g4--text font-weight-medium"
                            >
                                {{ $t('from client portal') }}
                            </span>
                        </div>
                        <v-tooltip bottom>
                            <template #activator="{ on }">
                                <v-btn
                                    v-if="isMasterPopup"
                                    text
                                    x-small
                                    style="height: 16px"
                                    class="caption ml-auto mr-1 text-none"
                                    color="primary"
                                    v-on="on"
                                    @click.stop.prevent="$emit('hide-all')"
                                >
                                    {{ $t('Hide all') }} ({{ notificationCount >= 10 ? '9+' : notificationCount }})
                                </v-btn>
                            </template>
                            {{ $t('Hide for 1 hour') }}
                        </v-tooltip>

                    </v-list-item-subtitle>
                    <div style="position: absolute; right: 2px; top: 2px;" @click.stop.prevent="">
                        <icon-button
                            v-if="isPopup"
                            x-small
                            icon-size="20"
                            icon="X"
                            @click="onNotificationCloseClick(notification)"
                        />
                    </div>
                </div>
                <div
                    v-if="!isPopup && !notification.is_seen"
                    class="rounded-circle primary"
                    style="min-width: 10px; min-height: 10px; max-width: 10px; max-height: 10px"
                />
            </v-list-item-content>
            <div
                v-if="isPopup && !isLatestNotification"
                class="multi-sticky rounded-lg elevation-4"
                :class="notificationCount > 1 && showMasterPopup ? 'sticky-visible' : ''"
            />
        </v-list-item>
    </transition>
</template>

<script>
import AccountAvatar from "../../components/AccountAvatar";
import errorDialog from "@/views/ErrorDialog/errorDialog"
import NotificationModule from "../../store/modules/notification";
import { formatLastModifiedHtml } from "../../plugins/formatting";
import {computed} from "@vue/composition-api";
import {VFadeTransition} from "vuetify/lib";

export default {
    name: "NotificationListItem",
    components: {
        AccountAvatar,
        VFadeTransition
    },
    props: {
        notification: {
            type: Object,
            required: true
        },
        isPopup: {
            type: Boolean
        },
        notificationCount: {
            type: Number,
            default: null
        },
        isHovered: {
            type: Boolean,
            default: false
        },
        showMasterPopup: {
            type: Boolean,
            default: false
        },
        isMasterPopup: {
            type: Boolean,
            default: false
        },
        isLatestNotification: {
            type: Boolean,
            default: false
        }
    },
    setup(props, ctx) {
        const {
            navigateToNotificationSource,
            isStickyNotification,
            markNotificationsSeen,
            latestNotifications
        } = NotificationModule();

        if (props.isLatestNotification) {
            setTimeout(() => {
                latestNotifications.value = latestNotifications.value.filter(x => x.id !== props.notification.id)
            }, 5000);
        }

        const isCpNotification = computed(() => {
            const cpNotificationTypes = [
                'task_message',
                'request_created',
                'task_created_summary_email',
                'task_auto_finish_failed'
            ]

            return props.notification && props.notification.activity && cpNotificationTypes.includes(props.notification.activity.type)
        })

        const isCpCommunicationNotification = computed(() => {
            const cpNotificationTypes = [
                'task_message',
                'task_created_summary_email',
                'request_created',
                'task_message_from_project_email',
                'task_message_from_client_email',
                'tasm_message_from_task_email',
                'task_created_client_email',
                'task_created_project_email',
                'task_created_email',
                'message_from_email'
            ]

            return props.notification && props.notification.activity && cpNotificationTypes.includes(props.notification.activity.type)
        })

        const onNotificationClick = item => {
            const notification_id_list = [item.id];
            markNotificationsSeen(notification_id_list).catch(ex => errorDialog.showError(ex));

            navigateToNotificationSource(item);

        };
        const onNotificationCloseClick = item => {
            if (!item || !item.id) return;
            let notification_id_list = [item.id];
            markNotificationsSeen(notification_id_list).catch(ex => errorDialog.showError(ex));
        };

        const showAccountAvatar = () => {
            const has_creator_initials = props.notification.creator_initials !== "-"
            return props.notification.activity && props.notification.activity.creator_id || has_creator_initials
        }

        return {
            isStickyNotification,
            onNotificationClick,
            onNotificationCloseClick,
            formatLastModifiedHtml,
            isCpNotification,
            isCpCommunicationNotification,
            showAccountAvatar,
        };
    }
};
</script>

<style lang="scss">
.v-list-item {
    &.fade-transition-enter-active, &.fade-transition-leave-active {
        .multi-sticky {
            transition: unset !important;
            opacity: 0 !important;
            transform: translate(-5px, 5px);
        }
    }
}
</style>

<style scoped lang="scss">
.v-list-item {
    position: relative;
    z-index: unset !important;
    background: var(--v-inverted-base);
    border-radius: 8px;
    margin-bottom: 1px;
    margin-top: 1px;

    &::before {
        border-radius: 8px;
    }

    &.sticky {
        max-width: 360px;
        width: 360px;
    }

    &.cp-communication-notification {
        background: var(--v-orange-lighten2) !important;
    }

    .multi-sticky {
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        top: 0;
        background: white;
        z-index: -1;
        transition: all 0.3s ease-in-out;

        &.sticky-visible {
            transform: translate(-5px, 5px);
        }
    }

    .v-list-item__avatar {
        overflow: visible;

        .cp-avatar-bubble {
            width: 24px;
            height: 24px;
            background: var(--v-gradient-base) !important;
            position: absolute;
            right: -4px;
            bottom: -4px;
            display: flex;
            align-items: center;
            justify-content: center;
        }
    }
}
</style>